import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Paper,
  Typography,
  Divider,
  CircularProgress,
  Chip,
} from "@material-ui/core";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import IconButton from "@material-ui/core/IconButton";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const metricCardStyle = {
  padding: 12,
  minHeight: 88,
};

const ContactOverviewModal = ({ open, contactId, onClose }) => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    const fetchOverview = async () => {
      if (!open || !contactId) return;
      setLoading(true);
      try {
        const { data } = await api.get(`/contacts/${contactId}/overview`);
        setOverview(data);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchOverview();
  }, [open, contactId]);

  const tickets = useMemo(() => overview?.tickets || [], [overview]);
  const metrics = overview?.metrics || {};
  const contact = overview?.contact;

  const getStatusLabel = status => {
    if (status === "open") return i18n.t("contacts.overview.status.open");
    if (status === "pending") return i18n.t("contacts.overview.status.pending");
    if (status === "closed") return i18n.t("contacts.overview.status.closed");
    return status || "-";
  };

  const openTicket = ticketId => {
    onClose();
    history.push(`/tickets/${ticketId}`);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle>{i18n.t("contacts.overview.title")}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <CircularProgress size={28} />
          </div>
        ) : (
          <>
            <Typography variant="h6">{contact?.name || "-"}</Typography>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("contacts.overview.contactMeta", {
                number: contact?.number || "-",
                email: contact?.email || "-"
              })}
            </Typography>
            <Divider style={{ margin: "12px 0" }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper variant="outlined" style={metricCardStyle}>
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("contacts.overview.metrics.total")}
                  </Typography>
                  <Typography variant="h5">{metrics.totalTickets || 0}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper variant="outlined" style={metricCardStyle}>
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("contacts.overview.metrics.open")}
                  </Typography>
                  <Typography variant="h5">{metrics.openTickets || 0}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper variant="outlined" style={metricCardStyle}>
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("contacts.overview.metrics.pending")}
                  </Typography>
                  <Typography variant="h5">{metrics.pendingTickets || 0}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper variant="outlined" style={metricCardStyle}>
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("contacts.overview.metrics.closed")}
                  </Typography>
                  <Typography variant="h5">{metrics.closedTickets || 0}</Typography>
                </Paper>
              </Grid>
            </Grid>

            <Typography variant="subtitle1" style={{ marginTop: 20, marginBottom: 8 }}>
              {i18n.t("contacts.overview.ticketsTitle")}
            </Typography>

            {tickets.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                {i18n.t("contacts.overview.noTickets")}
              </Typography>
            ) : (
              tickets.map(ticket => (
                <Paper
                  key={ticket.id}
                  variant="outlined"
                  style={{ padding: 12, marginBottom: 8 }}
                >
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={10}>
                      <Typography variant="subtitle2">
                        #{ticket.id} - {ticket.subject || i18n.t("contacts.overview.noSubject")}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(ticket.updatedAt || ticket.createdAt).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={2} style={{ textAlign: "right" }}>
                      <IconButton size="small" onClick={() => openTicket(ticket.id)}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Grid>
                    <Grid item xs={12}>
                      <Chip
                        size="small"
                        label={getStatusLabel(ticket.status)}
                        style={{ marginRight: 8 }}
                      />
                      {ticket.queue?.name ? (
                        <Chip size="small" label={`Fila: ${ticket.queue.name}`} />
                      ) : null}
                    </Grid>
                    {ticket.lastMessage ? (
                      <Grid item xs={12}>
                        <Typography variant="body2">{ticket.lastMessage}</Typography>
                      </Grid>
                    ) : null}
                  </Grid>
                </Paper>
              ))
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="outlined">
          {i18n.t("contacts.overview.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactOverviewModal;
